import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import {
  PRELAUNCH_PRIVACY_ACKNOWLEDGEMENT,
  PRELAUNCH_TERMS_ACKNOWLEDGEMENT,
} from "@bw/shared";

import { resubmitOrg, type OnboardingState } from "@/libs/onboardingApi";

interface RejectedPageProps {
  state: OnboardingState;
}

export function RejectedPage({ state }: RejectedPageProps) {
  const { display, rejection, submission_summary, alerts } = state;
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [fullName, setFullName] = useState(
    submission_summary?.owner_full_name ?? "",
  );
  const [phone, setPhone] = useState(submission_summary?.owner_phone ?? "");
  const [businessName, setBusinessName] = useState(
    submission_summary?.business_name ?? "",
  );
  const [tosAccepted, setTosAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resubmit = useMutation({
    mutationFn: () =>
      resubmitOrg(state.org_id, {
        business_name: businessName.trim(),
        legal_name: businessName.trim(),
        country: submission_summary?.country ?? "US",
        owner_full_name: fullName.trim(),
        owner_email: user?.email ?? submission_summary?.owner_email ?? "",
        owner_phone: phone.trim(),
        address_line1: "",
        city: "",
        state: "",
        postal_code: "",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org", "me"] });
      navigate("/");
    },
    onError: (err) => {
      setError(
        isApiError(err)
          ? err.message || "Something went wrong. Please try again."
          : "Something went wrong. Please try again.",
      );
    },
  });

  const canSubmit =
    fullName.trim() !== "" &&
    businessName.trim() !== "" &&
    tosAccepted &&
    privacyAccepted &&
    !resubmit.isPending;

  if (showForm) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 560,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: 6,
        }}
      >
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack spacing={2.5}>
            <Stack spacing={0.75}>
              <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                Update &amp; resubmit
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Correct your application details and submit them for another
                review.
              </Typography>
            </Stack>

            {error ? (
              <Alert severity="error" role="alert">
                {error}
              </Alert>
            ) : null}

            <Stack
              component="form"
              spacing={2}
              onSubmit={(event) => {
                event.preventDefault();
                if (canSubmit) resubmit.mutate();
              }}
            >
              <TextField
                label="Full name"
                onChange={(event) => setFullName(event.target.value)}
                placeholder="Your full name"
                required
                value={fullName}
              />
              <TextField
                label="Phone (optional)"
                onChange={(event) => setPhone(event.target.value)}
                placeholder="+1 (555) 000-0000"
                type="tel"
                value={phone}
              />
              <TextField
                label="Business name"
                onChange={(event) => setBusinessName(event.target.value)}
                placeholder="Your business name"
                required
                value={businessName}
              />
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tosAccepted}
                      onChange={(event) => setTosAccepted(event.target.checked)}
                    />
                  }
                  label={PRELAUNCH_TERMS_ACKNOWLEDGEMENT}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={privacyAccepted}
                      onChange={(event) =>
                        setPrivacyAccepted(event.target.checked)
                      }
                    />
                  }
                  label={PRELAUNCH_PRIVACY_ACKNOWLEDGEMENT}
                />
              </Stack>
              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={1.5}
              >
                <Button
                  disabled={resubmit.isPending}
                  fullWidth
                  onClick={() => setShowForm(false)}
                  type="button"
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!canSubmit}
                  fullWidth
                  type="submit"
                  variant="contained"
                >
                  {resubmit.isPending ? "Submitting…" : "Resubmit application"}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Stack
      spacing={2.5}
      sx={{
        width: "100%",
        maxWidth: 560,
        mx: "auto",
        px: { xs: 2, sm: 3 },
        py: 6,
      }}
    >
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={1.5} sx={{ alignItems: "center", textAlign: "center" }}>
          <Avatar
            sx={{
              width: 58,
              height: 58,
              bgcolor: "error.main",
              color: "error.contrastText",
            }}
          >
            <CancelOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
            {display.title ?? "Registration rejected"}
          </Typography>
          {display.subtitle ? (
            <Typography color="text.secondary" variant="body2">
              {display.subtitle}
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      {rejection?.reason ? (
        <Alert severity="error">
          <AlertTitle>Reason for rejection</AlertTitle>
          {rejection.reason}
        </Alert>
      ) : null}

      {alerts.map((message, index) => (
        <Alert key={`${message}-${index}`} severity="info">
          {message}
        </Alert>
      ))}

      {submission_summary ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <Typography
              component="h2"
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
            >
              Your submission
            </Typography>
            <Box
              component="dl"
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(120px, auto) minmax(0, 1fr)",
                columnGap: 2,
                rowGap: 1.25,
                m: 0,
              }}
            >
              <Typography component="dt" color="text.secondary" variant="body2">
                Business name
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                {submission_summary.business_name}
              </Typography>
              <Typography component="dt" color="text.secondary" variant="body2">
                Country
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                {submission_summary.country}
              </Typography>
              <Typography component="dt" color="text.secondary" variant="body2">
                Owner email
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                sx={{ m: 0, overflowWrap: "anywhere" }}
              >
                {submission_summary.owner_email}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      ) : null}

      {rejection?.can_resubmit ? (
        <Button fullWidth onClick={() => setShowForm(true)} variant="contained">
          Update &amp; resubmit
        </Button>
      ) : null}
    </Stack>
  );
}
